import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import withTranslation from 'hoc/withTranslation';
import { calculateMaxDistance, getEVProfileName, fetchEVProfile } from '../../../utils/evProfiles';
import useEVData from './EV/useEVData';
import EVDropdowns from './EV/EVDropdowns';
import EVSpecifications from './EV/EVSpecifications';
import EVChargingInputs from './EVChargingInputs';
import Label from '../../Form/Label';
import NumericInput from '../Global/NumericInput';
import { StyledSelectBigInput } from '../Global/WizardStyled';

const StyledEVContainer = styled.div`
  background: rgba(34, 205, 200, 0.05);
  padding: 1rem;
  border-radius: 0.12rem;
  border: 1px solid rgba(34, 205, 200, 0.3);
  margin-bottom: 1rem;
`;

const StyledModeWrapper = styled.div({
  marginBottom: '1rem',
});

const StyledMaxDistanceField = styled.div({
  width: 'calc(50% - 0.5rem)',
  paddingTop: '0.5rem',
  marginBottom: '1rem',
});

const EVSelector = ({
  oAuth,
  apiKey,
  onEVProfileChange,
  onAuthError,
  initialProfile,
  charging,
  vehicleAmount,
  onChargingChange,
  translations: {
    wizard: {
      fleet: {
        evRechargeMode,
        evRechargeModeConsumption,
        evRechargeModeMaxDistance,
        evMaxDistanceInput,
      },
    },
  },
}) => {
  const [evMode, setEvMode] = useState(initialProfile?.evMode || 'consumption');
  const [maxDistanceKm, setMaxDistanceKm] = useState(
    initialProfile?.evMode === 'maxDistance' && initialProfile?.maxDistance
      ? initialProfile.maxDistance / 1000
      : '',
  );

  const {
    makes,
    models,
    variants,
    selectedMake,
    selectedModel,
    selectedVariant,
    setSelectedMake,
    setSelectedModel,
    setSelectedVariant,
    selectedVariantData,
    authError,
  } = useEVData(oAuth, apiKey, initialProfile);

  const hasUserInteracted = useRef(!initialProfile);

  const onAuthErrorRef = useRef(onAuthError);
  const onEVProfileChangeRef = useRef(onEVProfileChange);
  const maxDistanceKmRef = useRef(maxDistanceKm);
  const profileInfoRef = useRef(null);

  useEffect(() => {
    onAuthErrorRef.current = onAuthError;
  }, [onAuthError]);

  useEffect(() => {
    onEVProfileChangeRef.current = onEVProfileChange;
  }, [onEVProfileChange]);

  useEffect(() => {
    maxDistanceKmRef.current = maxDistanceKm;
  }, [maxDistanceKm]);

  useEffect(() => {
    if (selectedMake && selectedModel && selectedVariant && selectedVariantData) {
      profileInfoRef.current = {
        profileId: selectedVariantData.profileId,
        make: makes.find((m) => m.id === selectedMake)?.name || selectedMake,
        model: models.find((m) => m.id === selectedModel)?.name || selectedModel,
        variant: selectedVariantData.versions?.[0]?.label || selectedVariantData.profileId,
      };
    } else {
      profileInfoRef.current = null;
    }
  }, [selectedMake, selectedModel, selectedVariant, selectedVariantData, makes, models]);

  useEffect(() => {
    if (evMode === 'consumption' && authError && onAuthErrorRef.current) {
      onAuthErrorRef.current();
    }
  }, [evMode, authError]);

  useEffect(() => {
    if (evMode !== 'consumption') return;
    if (selectedMake && selectedModel && selectedVariant && !isEmpty(variants)) {
      if (selectedVariantData) {
        hasUserInteracted.current = true;
        const makeName = makes.find((m) => m.id === selectedMake)?.name || selectedMake;
        const modelName = models.find((m) => m.id === selectedModel)?.name || selectedModel;
        const variantName =
          selectedVariantData.versions?.[0]?.label || selectedVariantData.profileId;
        const maxDistance = calculateMaxDistance(selectedVariantData.range);

        const loadProfile = async () => {
          const profileData = await fetchEVProfile(selectedVariantData.profileId, oAuth, apiKey);
          const batteryCapacity = profileData?.batteryCapacityKwh ?? null;
          onEVProfileChangeRef.current({
            evMode: 'consumption',
            profileId: selectedVariantData.profileId,
            maxDistance,
            make: makeName,
            model: modelName,
            variant: variantName,
            profileName: getEVProfileName(makeName, modelName, variantName),
            specs: {
              range: selectedVariantData.range,
              unladenWeight: selectedVariantData.unladenWeight,
              grossWeight: selectedVariantData.grossWeight,
              width: selectedVariantData.width,
              height: selectedVariantData.height,
              releaseYear: selectedVariantData.releaseYear,
              bodyType: selectedVariantData.bodyType,
              lengthClasses: selectedVariantData.lengthClasses,
              heightClasses: selectedVariantData.heightClasses,
              weightClasses: selectedVariantData.weightClasses,
              batteryCapacity,
            },
          });
        };
        loadProfile();
      }
    } else if (hasUserInteracted.current) {
      onEVProfileChangeRef.current(null);
    }
  }, [evMode, selectedMake, selectedModel, selectedVariant, variants, makes, models, selectedVariantData]);

  useEffect(() => {
    if (evMode !== 'maxDistance') return;
    const meters = maxDistanceKmRef.current ? maxDistanceKmRef.current * 1000 : null;
    const info = profileInfoRef.current;
    if (selectedMake && selectedModel && selectedVariant && selectedVariantData) {
      hasUserInteracted.current = true;
      onEVProfileChangeRef.current({
        evMode: 'maxDistance',
        maxDistance: meters,
        profileId: info?.profileId,
        make: info?.make,
        model: info?.model,
        variant: info?.variant,
      });
    } else if (hasUserInteracted.current) {
      onEVProfileChangeRef.current({ evMode: 'maxDistance', maxDistance: meters });
    }
  }, [evMode, selectedMake, selectedModel, selectedVariant, selectedVariantData]);

  const handleMakeChange = useCallback(
    (e) => {
      setSelectedMake(e.target.value);
    },
    [setSelectedMake],
  );

  const handleModelChange = useCallback(
    (e) => {
      setSelectedModel(e.target.value);
    },
    [setSelectedModel],
  );

  const handleVariantChange = useCallback(
    (e) => {
      setSelectedVariant(e.target.value);
    },
    [setSelectedVariant],
  );

  const handleModeChange = useCallback((e) => {
    const newMode = e.target.value;
    setEvMode(newMode);
    if (newMode === 'maxDistance') {
      const meters = maxDistanceKmRef.current ? maxDistanceKmRef.current * 1000 : null;
      const info = profileInfoRef.current;
      onEVProfileChangeRef.current({
        evMode: 'maxDistance',
        maxDistance: meters,
        ...(info && {
          profileId: info.profileId,
          make: info.make,
          model: info.model,
          variant: info.variant,
        }),
      });
    }
  }, []);

  const handleMaxDistanceChange = useCallback((val) => {
    setMaxDistanceKm(val);
    const meters = val ? val * 1000 : null;
    const info = profileInfoRef.current;
    onEVProfileChangeRef.current({
      evMode: 'maxDistance',
      maxDistance: meters,
      ...(info && {
        profileId: info.profileId,
        make: info.make,
        model: info.model,
        variant: info.variant,
      }),
    });
  }, []);

  return (
    <StyledEVContainer>
      <StyledModeWrapper>
        <Label>{evRechargeMode}</Label>
        <StyledSelectBigInput
          fullWidth
          value={evMode}
          onChange={handleModeChange}
          id="input-ev-recharge-mode"
        >
          <option value="consumption">{evRechargeModeConsumption}</option>
          <option value="maxDistance">{evRechargeModeMaxDistance}</option>
        </StyledSelectBigInput>
      </StyledModeWrapper>
      {evMode === 'consumption' ? (
        <>
          <EVDropdowns
            makes={makes}
            models={models}
            variants={variants}
            selectedMake={selectedMake}
            selectedModel={selectedModel}
            selectedVariant={selectedVariant}
            onMakeChange={handleMakeChange}
            onModelChange={handleModelChange}
            onVariantChange={handleVariantChange}
          />
          <EVChargingInputs
            charging={charging}
            vehicleAmount={vehicleAmount}
            onChargingChange={onChargingChange}
          />
          <EVSpecifications variantData={selectedVariantData} />
        </>
      ) : (
        <>
          <StyledMaxDistanceField>
            <Label>{evMaxDistanceInput}</Label>
            <NumericInput
              id="input-ev-max-distance"
              initialValue={maxDistanceKm}
              minValue={1}
              maxValue={100000}
              integer
              onChange={handleMaxDistanceChange}
            />
          </StyledMaxDistanceField>
          <EVDropdowns
            makes={makes}
            models={models}
            variants={variants}
            selectedMake={selectedMake}
            selectedModel={selectedModel}
            selectedVariant={selectedVariant}
            onMakeChange={handleMakeChange}
            onModelChange={handleModelChange}
            onVariantChange={handleVariantChange}
          />
        </>
      )}
    </StyledEVContainer>
  );
};

export default withTranslation(EVSelector);
